import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import { 
  MessageCircle, Send, User, Settings2,
  CheckCircle, XCircle, Loader2, RefreshCw, UserCheck, Save, Trash2
} from 'lucide-react';
import { useAdminAuth } from '../context/AdminAuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const WS_URL = BACKEND_URL.replace(/^http/, 'ws') + '/api/ws/chat';

const AdminChat = () => {
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [wsConnected, setWsConnected] = useState(false);
  const [agentName, setAgentName] = useState('Customer Care');
  const [editingAgentName, setEditingAgentName] = useState(false);
  const [tempAgentName, setTempAgentName] = useState('');
  const [replyName, setReplyName] = useState('');
  const [acceptNameInput, setAcceptNameInput] = useState({});
  const messagesEndRef = useRef(null);
  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const { authenticated, loading: authLoading, logout, currentUser, getAuthHeader } = useAdminAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!authLoading && !authenticated) navigate('/admin/login');
  }, [authenticated, authLoading, navigate]);

  useEffect(() => {
    if (authenticated) {
      fetchConversations();
      fetchChatSettings();
      const interval = setInterval(fetchConversations, 10000);
      return () => clearInterval(interval);
    }
  }, [authenticated]);

  const fetchChatSettings = async () => {
    try {
      const res = await axios.get(`${API}/admin/chat/settings`, { headers: getAuthHeader() });
      if (res.data.settings?.agent_name) setAgentName(res.data.settings.agent_name);
    } catch {}
  };

  const saveAgentName = async () => {
    try {
      await axios.put(`${API}/admin/chat/settings`, { agent_name: tempAgentName }, { headers: getAuthHeader() });
      setAgentName(tempAgentName);
      setEditingAgentName(false);
      toast.success('Agent name updated');
    } catch {
      toast.error('Failed to save agent name');
    }
  };

  const acceptChat = async (convId) => {
    const name = (acceptNameInput[convId] || '').trim();
    if (!name) {
      toast.error('Please enter your name before accepting');
      return;
    }
    try {
      await axios.put(`${API}/admin/chat/conversations/${convId}`, { accept: true, agent_name: name }, { headers: getAuthHeader() });
      setReplyName(name);
      setAcceptNameInput(prev => { const n = {...prev}; delete n[convId]; return n; });
      fetchConversations();
      toast.success(`Chat accepted as "${name}"`);
    } catch {
      toast.error('Failed to accept chat');
    }
  };

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // WebSocket connection for selected conversation
  const connectWebSocket = useCallback((convId) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.close();
    }

    const ws = new WebSocket(`${WS_URL}/${convId}`);
    wsRef.current = ws;

    ws.onopen = () => {
      setWsConnected(true);
      const pingInterval = setInterval(() => {
        if (ws.readyState === WebSocket.OPEN) ws.send('ping');
      }, 30000);
      ws._pingInterval = pingInterval;
    };

    ws.onmessage = (event) => {
      if (event.data === 'pong') return;
      try {
        const data = JSON.parse(event.data);
        if (data.type === 'new_message') {
          setMessages(prev => {
            const exists = prev.some(m => m._id === data.message._id);
            if (exists) return prev;
            return [...prev, data.message];
          });
          fetchConversations();
        } else if (data.type === 'chat_closed') {
          setSelectedConversation(prev => prev ? {...prev, status: 'closed'} : prev);
          fetchConversations();
        } else if (data.type === 'chat_accepted') {
          setSelectedConversation(prev => prev ? {...prev, status: 'active', assigned_agent: data.assigned_agent} : prev);
          fetchConversations();
        }
      } catch (e) {
        console.error('WS parse error:', e);
      }
    };

    ws.onclose = () => {
      setWsConnected(false);
      clearInterval(ws._pingInterval);
      reconnectTimeoutRef.current = setTimeout(() => {
        if (selectedConversation?._id === convId) connectWebSocket(convId);
      }, 3000);
    };

    ws.onerror = () => ws.close();
  }, [selectedConversation]);

  // Cleanup WS on unmount or conversation change
  useEffect(() => {
    return () => {
      if (wsRef.current) {
        clearInterval(wsRef.current._pingInterval);
        wsRef.current.close();
        wsRef.current = null;
      }
      if (reconnectTimeoutRef.current) clearTimeout(reconnectTimeoutRef.current);
    };
  }, []);

  const fetchConversations = async () => {
    try {
      const response = await axios.get(`${API}/admin/chat/conversations`, {
        headers: getAuthHeader()
      });
      setConversations(response.data.conversations || []);
    } catch (error) {
      console.error('Error fetching conversations:', error);
    } finally {
      setLoading(false);
    }
  };

  const selectConversation = async (conv) => {
    // Close previous WS
    if (wsRef.current) {
      clearInterval(wsRef.current._pingInterval);
      wsRef.current.close();
      wsRef.current = null;
    }

    setSelectedConversation(conv);
    if (conv.assigned_agent) setReplyName(conv.assigned_agent);
    try {
      const response = await axios.get(`${API}/chat/conversations/${conv._id}`, {
        headers: getAuthHeader()
      });
      setMessages(response.data.conversation?.messages || []);
      
      await axios.put(`${API}/admin/chat/conversations/${conv._id}/read`, {}, {
        headers: getAuthHeader()
      });
      fetchConversations();

      // Connect WebSocket for this conversation
      connectWebSocket(conv._id);
    } catch (error) {
      toast.error('Failed to load messages');
    }
  };

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || !selectedConversation) return;

    const msgContent = newMessage;
    setNewMessage('');
    setSending(true);
    try {
      const response = await axios.post(`${API}/chat/messages`, {
        conversation_id: selectedConversation._id,
        content: msgContent,
        sender_type: 'admin',
        sender_name: replyName || agentName
      }, {
        headers: getAuthHeader()
      });

      if (response.data.success && !wsConnected) {
        setMessages(prev => {
          const exists = prev.some(m => m._id === response.data.message._id);
          if (exists) return prev;
          return [...prev, response.data.message];
        });
      }
    } catch (error) {
      toast.error('Failed to send message');
      setNewMessage(msgContent);
    } finally {
      setSending(false);
    }
  };

  const updateStatus = async (status) => {
    if (!selectedConversation) return;
    try {
      await axios.put(`${API}/admin/chat/conversations/${selectedConversation._id}`, { status }, {
        headers: getAuthHeader()
      });
      toast.success(`Conversation marked as ${status}`);
      fetchConversations();
      setSelectedConversation({ ...selectedConversation, status });
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const deleteConversation = async (convId, e) => {
    if (e) e.stopPropagation();
    if (!window.confirm('Delete this conversation and all its messages? This cannot be undone.')) return;
    try {
      await axios.delete(`${API}/admin/chat/conversations/${convId}`, { headers: getAuthHeader() });
      if (selectedConversation?._id === convId) {
        setSelectedConversation(null);
        setMessages([]);
        if (wsRef.current) { wsRef.current.close(); wsRef.current = null; }
      }
      fetchConversations();
      toast.success('Conversation deleted');
    } catch {
      toast.error('Failed to delete conversation');
    }
  };

  const formatTime = (dateString) => new Date(dateString).toLocaleString();

  const getStatusColor = (status) => {
    switch (status) {
      case 'open': return 'bg-green-100 text-green-700';
      case 'pending': return 'bg-yellow-100 text-yellow-700';
      case 'closed': return 'bg-gray-100 text-gray-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  if (authLoading || !authenticated) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-green-600" />
      </div>
    );
  }

  return (
    <div data-testid="admin-chat-page">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <h1 className="text-2xl font-bold">Customer Support Chat</h1>
          {wsConnected && selectedConversation && (
            <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded-full">Live</span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {editingAgentName ? (
            <div className="flex items-center gap-1">
              <Input
                value={tempAgentName}
                onChange={(e) => setTempAgentName(e.target.value)}
                className="h-8 w-40 text-sm"
                placeholder="Agent name"
                data-testid="agent-name-input"
              />
              <Button size="sm" variant="default" onClick={saveAgentName} className="h-8" data-testid="save-agent-name">
                <Save className="h-3.5 w-3.5" />
              </Button>
              <Button size="sm" variant="ghost" onClick={() => setEditingAgentName(false)} className="h-8">
                <XCircle className="h-3.5 w-3.5" />
              </Button>
            </div>
          ) : (
            <Button
              variant="outline"
              size="sm"
              onClick={() => { setTempAgentName(agentName); setEditingAgentName(true); }}
              data-testid="edit-agent-name"
            >
              <Settings2 className="h-4 w-4 mr-1" /> {agentName}
            </Button>
          )}
          <Button variant="outline" size="sm" onClick={fetchConversations}>
            <RefreshCw className="h-4 w-4 mr-2" /> Refresh
          </Button>
        </div>
      </div>

      <div>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 h-[calc(100vh-12rem)]">
          {/* Conversations List */}
          <Card className="lg:col-span-1 overflow-hidden">
            <CardHeader className="bg-gray-50 border-b">
              <CardTitle className="text-lg flex items-center gap-2">
                <MessageCircle className="h-5 w-5" />
                Conversations ({conversations.length})
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0 overflow-y-auto" style={{ maxHeight: 'calc(100vh - 16rem)' }}>
              {loading ? (
                <div className="p-8 text-center">
                  <Loader2 className="h-6 w-6 animate-spin mx-auto text-gray-400" />
                </div>
              ) : conversations.length === 0 ? (
                <div className="p-8 text-center text-gray-500">No conversations yet</div>
              ) : (
                conversations.map((conv) => (
                  <div
                    key={conv._id}
                    onClick={() => selectConversation(conv)}
                    className={`p-4 border-b cursor-pointer hover:bg-gray-50 transition-colors ${
                      selectedConversation?._id === conv._id ? 'bg-green-50 border-l-4 border-l-green-500' : ''
                    }`}
                    data-testid={`chat-conv-${conv._id}`}
                  >
                    <div className="flex items-start justify-between mb-1">
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                          <User className="h-4 w-4 text-green-600" />
                        </div>
                        <div>
                          <p className="font-semibold text-sm">{conv.customer_name}</p>
                          <p className="text-xs text-gray-500">{conv.customer_email}</p>
                        </div>
                      </div>
                      {conv.unread_admin > 0 && (
                        <span className="bg-red-500 text-white text-xs rounded-full px-2 py-0.5">
                          {conv.unread_admin}
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-gray-600 truncate">{conv.last_message || 'No messages'}</p>
                    <div className="flex items-center justify-between mt-2">
                      <div className="flex items-center gap-1.5">
                        <span className={`text-xs px-2 py-0.5 rounded-full ${getStatusColor(conv.status)}`}>
                          {conv.status}
                        </span>
                        {conv.assigned_agent && (
                          <span className="text-[10px] text-gray-500 flex items-center gap-0.5">
                            <UserCheck className="h-3 w-3" /> {conv.assigned_agent}
                          </span>
                        )}
                      </div>
                      {!conv.assigned_agent && conv.status === 'open' && (
                        <div className="flex flex-col gap-1 mt-1 w-full" onClick={(e) => e.stopPropagation()}>
                          <Input
                            value={acceptNameInput[conv._id] || ''}
                            onChange={(e) => setAcceptNameInput(prev => ({...prev, [conv._id]: e.target.value}))}
                            placeholder="Your name..."
                            className="h-7 text-xs"
                            data-testid={`accept-name-${conv._id}`}
                          />
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-6 text-xs px-2 text-green-600 w-full"
                            onClick={(e) => { e.stopPropagation(); acceptChat(conv._id); }}
                            data-testid={`accept-chat-${conv._id}`}
                          >
                            <UserCheck className="h-3 w-3 mr-1" /> Accept Chat
                          </Button>
                        </div>
                      )}
                      {(conv.assigned_agent || conv.status !== 'open') && (
                        <div className="flex items-center gap-1">
                          <span className="text-xs text-gray-400">
                            {new Date(conv.updated_at).toLocaleDateString()}
                          </span>
                          {conv.status === 'closed' && (
                            <button
                              onClick={(e) => deleteConversation(conv._id, e)}
                              className="p-1 hover:bg-red-50 rounded text-red-400 hover:text-red-600 transition-colors"
                              title="Delete conversation"
                              data-testid={`delete-conv-${conv._id}`}
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                ))
              )}
            </CardContent>
          </Card>

          {/* Chat Window */}
          <Card className="lg:col-span-2 flex flex-col overflow-hidden">
            {selectedConversation ? (
              <>
                <CardHeader className="bg-gray-50 border-b py-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                        <User className="h-5 w-5 text-green-600" />
                      </div>
                      <div>
                        <p className="font-semibold">{selectedConversation.customer_name}</p>
                        <p className="text-sm text-gray-500">{selectedConversation.customer_email}</p>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button size="sm" variant={selectedConversation.status === 'open' ? 'default' : 'outline'} onClick={() => updateStatus('open')}>
                        <CheckCircle className="h-4 w-4 mr-1" /> Open
                      </Button>
                      <Button size="sm" variant={selectedConversation.status === 'closed' ? 'default' : 'outline'} onClick={() => updateStatus('closed')}>
                        <XCircle className="h-4 w-4 mr-1" /> Close
                      </Button>
                      {selectedConversation.status === 'closed' && (
                        <Button size="sm" variant="outline" className="text-red-600 hover:bg-red-50" onClick={(e) => deleteConversation(selectedConversation._id, e)} data-testid="delete-selected-conv">
                          <Trash2 className="h-4 w-4 mr-1" /> Delete
                        </Button>
                      )}
                    </div>
                  </div>
                </CardHeader>

                <CardContent className="flex-1 overflow-y-auto p-4 space-y-3" style={{ maxHeight: 'calc(100vh - 22rem)' }}>
                  {messages.map((msg, idx) => (
                    <div key={msg._id || idx} className={`flex ${msg.sender_type === 'admin' ? 'justify-end' : 'justify-start'}`}>
                      <div className={`max-w-[70%] rounded-2xl px-4 py-2 ${
                        msg.sender_type === 'admin'
                          ? 'bg-green-600 text-white rounded-br-md'
                          : 'bg-gray-100 text-gray-800 rounded-bl-md'
                      }`}>
                        {msg.sender_type === 'admin' && msg.sender_name && (
                          <p className="text-xs font-semibold text-green-200 mb-0.5">{msg.sender_name}</p>
                        )}
                        <p>{msg.content}</p>
                        {msg.file_url && (
                          <a href={`${BACKEND_URL}${msg.file_url}`} target="_blank" rel="noopener noreferrer" className="text-xs underline block mt-1">
                            View Attachment
                          </a>
                        )}
                        <p className={`text-xs mt-1 ${msg.sender_type === 'admin' ? 'text-green-100' : 'text-gray-400'}`}>
                          {formatTime(msg.created_at)}
                        </p>
                      </div>
                    </div>
                  ))}
                  <div ref={messagesEndRef} />
                </CardContent>

                {selectedConversation.status === 'closed' ? (
                  <div className="p-4 border-t bg-gray-50 text-center" data-testid="admin-chat-closed-banner">
                    <div className="bg-gray-100 rounded-lg py-3 px-4">
                      <p className="text-sm font-medium text-gray-600">This conversation is closed</p>
                      <p className="text-xs text-gray-400 mt-0.5">Reopen it to continue chatting</p>
                    </div>
                  </div>
                ) : (
                <div className="p-4 border-t bg-gray-50">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-xs text-gray-500">Replying as:</span>
                    <Input
                      value={replyName}
                      onChange={(e) => setReplyName(e.target.value)}
                      className="h-7 text-xs w-40"
                      placeholder="Your name"
                      data-testid="reply-name-input"
                    />
                  </div>
                  <form onSubmit={sendMessage} className="flex gap-2">
                    <Input
                      value={newMessage}
                      onChange={(e) => setNewMessage(e.target.value)}
                      placeholder="Type your reply..."
                      className="flex-1"
                      data-testid="admin-chat-input"
                    />
                    <Button type="submit" className="bg-green-600 hover:bg-green-700" disabled={sending || !newMessage.trim() || !replyName.trim()} data-testid="admin-chat-send-btn">
                      {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                    </Button>
                  </form>
                </div>
                )}
              </>
            ) : (
              <div className="flex-1 flex items-center justify-center text-gray-500">
                <div className="text-center">
                  <MessageCircle className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                  <p>Select a conversation to start chatting</p>
                </div>
              </div>
            )}
          </Card>
        </div>
      </div>
    </div>
  );
};

export default AdminChat;
